import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-8 py-12">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
        <p className="text-gray-400 mb-8">Last updated: November 8, 2025</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using PropFlo ("the Service"), you agree to be bound by these Terms of Service 
              ("Terms"). If you do not agree to these Terms, please do not use the Service. These Terms apply 
              to all users, including real estate agents, brokers, and their clients.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
            <p className="mb-4">PropFlo provides:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>AI-powered lead qualification and scoring for real estate professionals</li>
              <li>Automated voice, SMS, and email communication with potential clients</li>
              <li>Integration with Follow Up Boss CRM, Retell AI, and other third-party services</li>
              <li>Property recommendations and market analysis tools</li>
              <li>Lead management and workflow automation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts and Registration</h2>
            <p className="mb-4">To use PropFlo, you must:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Be at least 18 years of age</li>
              <li>Be a licensed real estate professional or authorized representative</li>
              <li>Provide accurate, current, and complete account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="mt-4">
              You are responsible for all activities that occur under your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. AI-Powered Services</h2>
            <p className="mb-4">
              PropFlo uses artificial intelligence to qualify leads and generate communications. You acknowledge that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>AI-generated lead scores are recommendations, not guarantees</li>
              <li>You remain responsible for all client communications and decisions</li>
              <li>AI responses should be reviewed before relying on them for critical decisions</li>
              <li>The Service uses GPT-4o, Retell AI, and other third-party AI providers</li>
              <li>We continuously improve our AI models, which may affect scoring accuracy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Real Estate Professional Responsibilities</h2>
            <p className="mb-4">As a licensed real estate professional using PropFlo, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Comply with all applicable real estate laws and regulations in your jurisdiction</li>
              <li>Maintain proper licensing and credentials</li>
              <li>Verify all information before making representations to clients</li>
              <li>Use the Service in accordance with MLS rules and NAR Code of Ethics</li>
              <li>Not use the Service for discriminatory purposes</li>
              <li>Ensure compliance with fair housing laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Data and Privacy</h2>
            <p>
              Your use of PropFlo is also governed by our Privacy Policy. By using the Service, you consent to 
              the collection, use, and sharing of information as described in our Privacy Policy. We process lead 
              data in accordance with applicable privacy laws, including PIPEDA (Canada) and other regional regulations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Third-Party Integrations</h2>
            <p className="mb-4">
              PropFlo integrates with third-party services including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Retell AI:</strong> Voice call automation and conversation intelligence</li>
              <li><strong>Follow Up Boss:</strong> CRM and lead management</li>
              <li><strong>OpenAI:</strong> AI-powered text generation and analysis</li>
              <li><strong>Twilio:</strong> SMS and voice communication</li>
              <li><strong>Make.com:</strong> Workflow automation</li>
            </ul>
            <p className="mt-4">
              Your use of these integrated services is subject to their respective terms of service and privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Payment and Subscription</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Subscription fees are billed in advance on a monthly or annual basis</li>
              <li>All fees are non-refundable except as required by law</li>
              <li>We reserve the right to change pricing with 30 days notice</li>
              <li>Failed payments may result in service suspension</li>
              <li>You may cancel your subscription at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Acceptable Use Policy</h2>
            <p className="mb-4">You agree NOT to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any laws, including fair housing and anti-discrimination laws</li>
              <li>Attempt to gain unauthorized access to the Service or other accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Scrape, copy, or replicate the Service without permission</li>
              <li>Misrepresent your identity or affiliation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Intellectual Property</h2>
            <p>
              PropFlo and its original content, features, and functionality are owned by PropFlo and are protected 
              by international copyright, trademark, and other intellectual property laws. You may not copy, modify, 
              distribute, or create derivative works based on our Service without explicit written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Disclaimers and Limitation of Liability</h2>
            <p className="mb-4">
              <strong>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We do not guarantee lead conversion or business results</li>
              <li>AI lead scores are estimates and may not be 100% accurate</li>
              <li>We are not responsible for third-party service failures</li>
              <li>You use the Service at your own risk</li>
            </ul>
            <p className="mt-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, PROPFLO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless PropFlo, its affiliates, and their respective officers, 
              directors, employees, and agents from any claims, damages, losses, or expenses arising from your 
              use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. Termination</h2>
            <p>
              We may terminate or suspend your account immediately, without prior notice, for any violation of these 
              Terms. Upon termination, your right to use the Service will immediately cease. We will provide you with 
              your data upon request, subject to our data retention policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of New Brunswick, Canada, 
              without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of 
              New Brunswick.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of material changes 
              by posting the updated Terms on this page. Your continued use of the Service after changes constitutes 
              acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">16. Contact Information</h2>
            <p className="mb-4">
              For questions about these Terms, please contact us:
            </p>
            <ul className="list-none space-y-2 ml-4">
              <li><strong>Email:</strong> <a href="mailto:support@aipropflo.com" className="text-blue-400 hover:text-blue-300">support@aipropflo.com</a></li>
              <li><strong>Website:</strong> <a href="https://www.aipropflo.com" className="text-blue-400 hover:text-blue-300">www.aipropflo.com</a></li>
              <li><strong>Address:</strong> Saint John, NB, Canada</li>
            </ul>
          </section>

          <div className="mt-12 p-6 bg-blue-900/20 border border-blue-800 rounded-lg">
            <p className="text-sm text-gray-400">
              <strong className="text-white">Professional Disclaimer:</strong> PropFlo is a technology platform 
              that assists real estate professionals. We are not a real estate brokerage and do not provide real 
              estate services. All real estate services are provided by licensed REALTOR® professionals.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
